import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Home, Video, Calendar, Megaphone, CreditCard, Users, LogOut, MessageCircle, X, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { schedulesAPI } from '../services/api';
import { format, parseISO, isToday, startOfDay, endOfDay } from 'date-fns';

const navItems = [
  { to: '/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/crm', icon: Users, label: 'CRM' },
  { to: '/videos', icon: Video, label: 'Video Library' },
  { to: '/schedule', icon: Calendar, label: 'Schedule' },
  // FEATURE_DISABLED: Marketing tab - re-enable when ready
  // { to: '/marketing', icon: Megaphone, label: 'Marketing' },
  // FEATURE_DISABLED: Community tab - re-enable when ready
  // { to: '/community', icon: MessageCircle, label: 'Community' },
  // FEATURE_DISABLED: Subscription tab - re-enable when ready
  // { to: '/subscription', icon: CreditCard, label: 'Subscription' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Sidebar({ onClose, isMobile }) {
  const { studio, logout } = useAuth();

  const studioId = studio?.id || studio?.studioId;
  const studioName = studio?.name || 'Demo Fitness Studio';

  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  const todayDayOfWeek = today.getDay();

  const { data: todaySchedulesData } = useQuery({
    queryKey: ['schedules-today', studioId, format(today, 'yyyy-MM-dd')],
    queryFn: () => schedulesAPI.getSchedules(studioId, {
      startDate: format(todayStart, 'yyyy-MM-dd'),
      endDate: format(todayEnd, 'yyyy-MM-dd')
    }),
    enabled: !!studioId,
    refetchInterval: false,
  });

  const todayScheduleCount = (() => {
    if (!todaySchedulesData?.data?.schedules) return 0;
    const schedules = todaySchedulesData.data.schedules;
    let count = 0;
    schedules.forEach(schedule => {
      if (schedule.specificDate) {
        if (isToday(parseISO(schedule.specificDate))) count++;
      } else if (schedule.dayOfWeek === todayDayOfWeek) {
        count++;
      }
    });
    return count;
  })();

  const getShortId = (id) => {
    if (!id) return null;
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = ((hash << 5) - hash) + id.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash).toString().slice(-6).padStart(4, '0');
  };

  const shortId = studioId ? getShortId(studioId) : null;
  const handleNavClick = () => isMobile && onClose && onClose();

  return (
    <aside
      className="w-64 flex flex-col h-full relative"
      style={{
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border-subtle)',
      }}
    >
      {/* Subtle side highlight */}
      <div
        className="absolute top-0 right-0 w-px h-full pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, var(--border-muted), transparent, transparent)' }}
      />

      {/* Logo/Brand Header */}
      <div className="relative p-5" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        {/* Subtle peach glow background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, var(--primary-8) 0%, transparent 60%)' }}
        />

        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Logo Box with gradient */}
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, var(--primary-hover) 0%, var(--primary) 100%)',
                boxShadow: '0 4px 12px var(--primary-30), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              <span className="text-lg font-bold" style={{ color: 'var(--bg-base)' }}>M</span>
            </div>
            <div>
              <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                My Studio Class
              </h1>
              <p className="text-xs truncate max-w-[120px]" style={{ color: 'var(--text-muted)' }}>
                {studioName}
              </p>
            </div>
          </div>

          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-all duration-200"
              style={{ color: 'var(--text-muted)' }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--primary-10)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Studio ID Badge */}
        {shortId && (
          <div
            className="relative mt-3 px-3 py-1.5 rounded-lg text-center"
            style={{ background: 'var(--highlight-8)', border: '1px solid var(--border-subtle)' }}
          >
            <span className="text-xs tracking-wider" style={{ color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 500, letterSpacing: '0.05em' }}>
              Studio ID: <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{shortId}</span>
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item, index) => {
          const Icon = item.icon;
          const showBadge = item.to === '/schedule' && todayScheduleCount > 0;

          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={handleNavClick}
              style={{ animationDelay: `${index * 50}ms` }}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 animate-fadeUp"
            >
              {({ isActive }) => (
                <div
                  className="flex items-center gap-3 w-full rounded-xl"
                  style={{
                    margin: '-10px -16px',
                    padding: '10px 16px',
                    background: isActive ? 'var(--highlight-10)' : 'transparent',
                    border: isActive ? '1px solid var(--border-subtle)' : '1px solid transparent',
                  }}
                >
                  {/* Icon container */}
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200"
                    style={{ background: isActive ? 'var(--primary-12)' : 'transparent' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: isActive ? 'var(--primary)' : 'var(--text-muted)' }} />
                  </div>
                  {/* Label */}
                  <span
                    className="flex-1 whitespace-nowrap text-sm"
                    style={{
                      color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    {item.label}
                  </span>
                  {/* Badge */}
                  {showBadge && (
                    <span
                      className="text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                      style={{ background: 'var(--primary)', color: 'var(--bg-base)' }}
                    >
                      {todayScheduleCount}
                    </span>
                  )}
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <button
          onClick={() => { handleNavClick(); logout(); }}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl w-full transition-all duration-200"
          style={{ color: 'var(--text-muted)' }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            e.currentTarget.style.color = '#ef4444';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--text-muted)';
          }}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center">
            <LogOut className="w-5 h-5" />
          </div>
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}
